import {
  calculateDateFields,
  calculateExpirationFields,
} from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/calculateDateFields';
import { getAvailableOrderTypes } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getAvailableOrderTypes';
import { getFrequency } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getFrequency';
import { getOrderPolicyAcceptanceRequired } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getOrderPolicyAcceptanceRequired';
import { getValidDaysOfWeekBooleans } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getValidDaysOfWeekBooleans';
import {
  getBudgetPeriod,
  getBudgetResetType,
  getFormattedBudgetResetPeriod,
} from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/mealCreditBudget';
import { calculateConsecutiveMonthsBasedOnBudgetLength } from 'src/components/oneCorp/features/MealCredit/utils/calculateConsecutiveMonthsBasedOnBudgetLength';
import {
  ExpenseCommentState,
  IMealCreditFormFields,
  transformToYesOrNoOrOptionalString,
  transformToYesOrNoString,
} from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import { getLocationsExistence } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/mealCreditLocations';
import { IMealCredit } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseMealCreditsDetailsPage.utils';
import { CorpUIResponse } from 'src/types/Corp';
import { getInstanceMonthAvailability } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getInstanceMonthAvailability';
import { calculateMonthAvailableTo } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/calculateMonthAvailableTo';
import { getDailyPeriodStartFromEndDay } from 'src/components/oneCorp/features/MealCredit/utils/getDailyPeriodStartFromEndDay';
import { MealCreditInstanceType } from 'src/types/LineOfCredit';

export type MealCreditDataParams = {
  mealCredit: IMealCredit;
  corpData: CorpUIResponse;
};

export const generateMealCreditData = ({
  mealCredit,
  corpData,
}: MealCreditDataParams): Partial<IMealCreditFormFields> => {
  const orderPolicyId = corpData?.orderPolicyId;

  const {
    name,
    first_instance_utc: firstInstanceUtc,
    first_instance_end_date_utc: firstInstanceEndDateUtc,
    duration_minutes: durationMinutes,
    expiration,
    repeating,
    instance_type: instanceType,
    budget_reset_period: budgetResetPeriod,
    valid_days_of_week: validDaysOfWeek,
    locations,
    valid_order_types: validOrderTypes,
    order_policy_acceptance_required: orderPolicyAcceptanceRequired,
    display_budgets: displayBudgets,
    expense_codes_required: expenseCodesRequired,
    expense_codes_enabled: expenseCodesEnabled,
    expense_comment_state: expenseCommentState,
    predefined_reasons_enabled: predefinedReasonsEnabled,
    send_loc_welcome_email: sendLocWelcomeEmail,
  } = mealCredit;

  const {
    startTime,
    startTimeMeridian,
    endTime,
    endTimeMeridian,
    timeIntervalType,
    date,
  } = calculateDateFields({
    firstInstanceUtc,
    durationMinutes,
  });
  const { expiresOn, expirationField } = calculateExpirationFields({
    expiration,
    timeIntervalType,
  });

  const budgetLength = getBudgetPeriod({
    instanceType,
    budgetResetPeriod,
  });

  const budgetResetType = getBudgetResetType(instanceType, budgetResetPeriod);

  const refreshPeriodMonthsCount =
    calculateConsecutiveMonthsBasedOnBudgetLength({
      budgetLength,
      budgetResetType,
      budgetResetPeriod,
    });

  return {
    name,
    frequency: getFrequency(repeating),
    budgetLength,
    budgetResetPeriod: getFormattedBudgetResetPeriod(
      instanceType,
      budgetResetPeriod
    ),
    budgetResetType,
    startTime,
    startTimeMeridian,
    endTime,
    endTimeMeridian,
    timeIntervalType,
    date,
    expiresOn,
    expiration: expirationField,
    days: getValidDaysOfWeekBooleans(validDaysOfWeek),
    locationsExistence: getLocationsExistence(locations),
    availableOrderTypes: getAvailableOrderTypes(validOrderTypes),
    orderPolicyAcceptanceRequired: getOrderPolicyAcceptanceRequired({
      orderPolicyId,
      orderPolicyAcceptanceRequired,
    }),
    displayBudgets: transformToYesOrNoString(displayBudgets),
    expenseCodes: transformToYesOrNoOrOptionalString(
      expenseCodesRequired,
      expenseCodesEnabled
    ),
    expenseComments: ExpenseCommentState[expenseCommentState],
    expenseReason: transformToYesOrNoString(predefinedReasonsEnabled),
    sendLocWelcomeEmail: transformToYesOrNoString(sendLocWelcomeEmail),
    monthAvailableOn: getInstanceMonthAvailability({
      firstInstanceUtc,
      firstInstanceEndDateUtc,
      timeIntervalType,
    }),
    monthAvailableTo:
      instanceType === MealCreditInstanceType.MONTHLY
        ? getDailyPeriodStartFromEndDay(
            calculateMonthAvailableTo({
              firstInstanceUtc,
              firstInstanceEndDateUtc,
              refreshPeriodMonthsCount,
              durationMinutes,
            }),
            timeIntervalType
          )
        : null,
  };
};
